import { useState, useEffect } from 'react'
import {
  Plus,
  Search,
  Edit3,
  Trash2,
  X,
  Building,
  Save,
  AlertCircle,
  Tag,
  AlertTriangle,
  Grid3x3,
  List
} from 'lucide-react'
import { createClient } from '@supabase/supabase-js'
import { CurrencyInputV2 } from '../components/CurrencyInputV2'
import { useModal } from '../contexts/ModalContext'
import { useToast } from '../hooks/useToast'
import { ToastContainer } from '../components/Toast'
import Button from '../components/Button'
import { useAuth } from '../contexts/AuthContext'
import * as driverFunctions from '../utils/driverFunctions'
import ImoveisCardView from '../components/ImoveisCardView'
import ImoveisListView from '../components/ImoveisListView'

// Configuração do Supabase
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const supabase = createClient(supabaseUrl, supabaseKey)

// Interfaces baseadas no schema
interface Imovel {
  id: string
  nome: string
  endereco: string | null
  estado: string | null
  cidade: string | null
  tipo: string
  tipo_imovel: string
  valor_venda: number | null
  construtora_id: string | null
  unidades_disponiveis: number
  unidades_vendidas: number
  created_at: string
  // Dados relacionados (joins)
  construtora?: {
    nome: string
    cnpj: string
    contato_responsavel: string
  }
  // Dados calculados
  unidades_count?: number
}

interface Construtora {
  id: string
  nome: string
  cnpj: string
  contato_responsavel: string
}

// Estados brasileiros
const ESTADOS_BRASILEIROS = [
  { sigla: 'AC', nome: 'Acre' },
  { sigla: 'AL', nome: 'Alagoas' },
  { sigla: 'AP', nome: 'Amapá' },
  { sigla: 'AM', nome: 'Amazonas' },
  { sigla: 'BA', nome: 'Bahia' },
  { sigla: 'CE', nome: 'Ceará' },
  { sigla: 'DF', nome: 'Distrito Federal' },
  { sigla: 'ES', nome: 'Espírito Santo' },
  { sigla: 'GO', nome: 'Goiás' },
  { sigla: 'MA', nome: 'Maranhão' },
  { sigla: 'MT', nome: 'Mato Grosso' },
  { sigla: 'MS', nome: 'Mato Grosso do Sul' },
  { sigla: 'MG', nome: 'Minas Gerais' },
  { sigla: 'PA', nome: 'Pará' },
  { sigla: 'PB', nome: 'Paraíba' },
  { sigla: 'PR', nome: 'Paraná' },
  { sigla: 'PE', nome: 'Pernambuco' },
  { sigla: 'PI', nome: 'Piauí' },
  { sigla: 'RJ', nome: 'Rio de Janeiro' },
  { sigla: 'RN', nome: 'Rio Grande do Norte' },
  { sigla: 'RS', nome: 'Rio Grande do Sul' },
  { sigla: 'RO', nome: 'Rondônia' },
  { sigla: 'RR', nome: 'Roraima' },
  { sigla: 'SC', nome: 'Santa Catarina' },
  { sigla: 'SP', nome: 'São Paulo' },
  { sigla: 'SE', nome: 'Sergipe' },
  { sigla: 'TO', nome: 'Tocantins' }
]

interface TipoImovel {
  id: string
  nome: string
  created_at: string
}

export default function Imoveis() {
  const [imoveis, setImoveis] = useState<Imovel[]>([])
  const [construtoras, setConstrutoras] = useState<Construtora[]>([])
  const [tiposImoveis, setTiposImoveis] = useState<TipoImovel[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isTiposModalOpen, setIsTiposModalOpen] = useState(false)
  const [editingImovel, setEditingImovel] = useState<Imovel | null>(null)
  const [editingTipo, setEditingTipo] = useState<TipoImovel | null>(null)
  const [isViewMode, setIsViewMode] = useState(false)
  const [formData, setFormData] = useState({
    nome: '',
    endereco: '',
    estado: '',
    cidade: '',
    tipo: '',
    tipo_imovel: 'novo',
    valor_venda: '',
    construtora_id: '',
    unidades_disponiveis: '',
    unidades_vendidas: ''
  })
  const [tipoFormData, setTipoFormData] = useState({
    nome: ''
  })
  const [error, setError] = useState('')
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [imovelToDelete, setImovelToDelete] = useState<Imovel | null>(null)
  const { setIsModalOpen: setGlobalModalOpen } = useModal()
  const { toasts, showSuccess, showError } = useToast()
  const { user } = useAuth()
  const [estados, setEstados] = useState(ESTADOS_BRASILEIROS)
  const [cidades, setCidades] = useState<string[]>([])
  const [loadingEstados, setLoadingEstados] = useState(false)
  const [loadingCidades, setLoadingCidades] = useState(false)
  // Carregar viewMode do localStorage ou usar 'cards' como padrão
  const [viewMode, setViewMode] = useState<'cards' | 'list'>(() => {
    const saved = localStorage.getItem('imoveis_viewMode')
    return (saved === 'cards' || saved === 'list') ? saved : 'cards'
  })

  // Carregar dados
  useEffect(() => {
    loadImoveis()
    loadConstrutoras()
    loadTiposImoveis()
    loadEstadosBrasil()
  }, [])

  // Salvar viewMode no localStorage quando mudar
  useEffect(() => {
    localStorage.setItem('imoveis_viewMode', viewMode)
  }, [viewMode])

  const loadImoveis = async () => {
    try {
      // Carregar imóveis com dados da construtora e contagem de unidades
      const { data: imoveisData, error: imoveisError } = await supabase
        .from('imoveis')
        .select(`
          id,
          nome,
          endereco,
          estado,
          cidade,
          tipo,
          tipo_imovel,
          valor_venda,
          construtora_id,
          unidades_disponiveis,
          unidades_vendidas,
          created_at,
          construtora:construtoras (
            nome,
            cnpj,
            contato_responsavel
          )
        `)
        .order('created_at', { ascending: false })

      if (imoveisError) throw imoveisError

      // Os dados das unidades já vêm da tabela imoveis
      const imoveisWithCounts = (imoveisData || []).map(imovel => ({
        ...imovel,
        unidades_count: (imovel.unidades_disponiveis || 0) + (imovel.unidades_vendidas || 0),
        construtora: Array.isArray(imovel.construtora) ? imovel.construtora[0] : imovel.construtora
      }))

      setImoveis(imoveisWithCounts)
    } catch (err) {
      console.error('Erro ao carregar imóveis:', err)
      setError('Erro ao carregar imóveis')
    } finally {
      setLoading(false)
    }
  }

  const loadEstadosBrasil = async () => {
    try {
      setLoadingEstados(true)
      const response = await fetch('https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome')
      if (!response.ok) {
        throw new Error(`Falha ao carregar estados: ${response.status}`)
      }
      const data = await response.json()
      const estadosMapeados = (data || []).map((estado: any) => ({
        sigla: estado.sigla,
        nome: estado.nome
      }))
      if (estadosMapeados.length > 0) {
        setEstados(estadosMapeados)
      }
    } catch (err) {
      console.error('Erro ao carregar estados do IBGE:', err)
      // Mantém lista padrão de fallback
      setEstados(ESTADOS_BRASILEIROS)
    } finally {
      setLoadingEstados(false)
    }
  }

  const loadCidadesPorEstado = async (uf: string) => {
    if (!uf) {
      setCidades([])
      return
    }
    try {
      setLoadingCidades(true)
      const response = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`)
      if (!response.ok) {
        throw new Error(`Falha ao carregar cidades: ${response.status}`)
      }
      const data = await response.json()
      const nomesCidades = (data || []).map((cidade: any) => cidade.nome)
      setCidades(nomesCidades)
    } catch (err) {
      console.error('Erro ao carregar cidades do IBGE:', err)
      setCidades([])
    } finally {
      setLoadingCidades(false)
    }
  }

  const handleEstadoChange = (uf: string) => {
    setFormData(prev => ({
      ...prev,
      estado: uf,
      cidade: ''
    }))
    if (uf) {
      loadCidadesPorEstado(uf)
    } else {
      setCidades([])
    }
  }

  const loadConstrutoras = async () => {
    try {
      const { data, error } = await supabase
        .from('construtoras')
        .select('*')
        .order('nome')

      if (error) throw error
      setConstrutoras(data || [])
    } catch (err) {
      console.error('Erro ao carregar construtoras:', err)
    }
  }

  const loadTiposImoveis = async () => {
    try {
      const { data, error } = await supabase
        .from('tipos_imoveis')
        .select('*')
        .order('nome')

      if (error) throw error
      setTiposImoveis(data || [])
    } catch (err) {
      console.error('Erro ao carregar tipos de imóveis:', err)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    try {
      const nomeImovel = formData.nome?.trim() || ''
      const enderecoImovel = formData.endereco?.trim() || ''

      // Validar dados obrigatórios
      if (!nomeImovel) {
        setError('Nome do imóvel é obrigatório')
        return
      }

      if (!formData.tipo || formData.tipo.trim() === '') {
        setError('Tipo do imóvel é obrigatório')
        return
      }

      if (!formData.valor_venda || parseFloat(formData.valor_venda) <= 0) {
        setError('Valor de venda deve ser maior que zero')
        return
      }

      if (!formData.construtora_id || formData.construtora_id.trim() === '') {
        setError('Construtora é obrigatória')
        return
      }

      if (editingImovel) {
        // Atualizar imóvel existente usando função segura
        const { error } = await supabase
          .rpc('atualizar_imovel_seguro', {
            p_id: editingImovel.id,
            p_nome: nomeImovel,
            p_endereco: enderecoImovel,
            p_tipo: formData.tipo,
            p_tipo_imovel: formData.tipo_imovel,
            p_valor_venda: parseFloat(formData.valor_venda),
            p_construtora_id: formData.construtora_id,
            p_estado: formData.estado || null,
            p_cidade: formData.cidade || null,
            p_unidades_disponiveis: parseInt(formData.unidades_disponiveis) || 0,
            p_unidades_vendidas: parseInt(formData.unidades_vendidas) || 0
          })

        if (error) {
          console.error('Erro ao atualizar imóvel:', error)
          if (error.message?.includes('obrigatório')) {
            setError(error.message)
          } else if (error.message?.includes('não encontrado')) {
            setError('Imóvel não encontrado ou não pertence a você')
          } else {
            setError('Erro ao atualizar imóvel: ' + error.message)
          }
          return
        }
      } else {
        // Criar novo imóvel usando função segura
        const { data: imovelId, error } = await supabase
          .rpc('criar_imovel_seguro', {
            p_nome: nomeImovel,
            p_endereco: enderecoImovel,
            p_tipo: formData.tipo,
            p_tipo_imovel: formData.tipo_imovel,
            p_valor_venda: parseFloat(formData.valor_venda),
            p_construtora_id: formData.construtora_id,
            p_estado: formData.estado || null,
            p_cidade: formData.cidade || null,
            p_unidades_disponiveis: parseInt(formData.unidades_disponiveis) || 0,
            p_unidades_vendidas: parseInt(formData.unidades_vendidas) || 0
          })

        if (error) {
          console.error('Erro ao criar imóvel:', error)
          if (error.message?.includes('obrigatório')) {
            setError(error.message)
          } else if (error.message?.includes('maior que zero')) {
            setError('Valor de venda deve ser maior que zero')
          } else {
            setError('Erro ao criar imóvel: ' + error.message)
          }
          return
        }

        console.log('Imóvel criado com sucesso! ID:', imovelId)

        if (user?.id) {
          try {
            await driverFunctions.createFolder(nomeImovel, null, user.id)
            console.log('Pasta criada no Meu Driver para o imóvel:', nomeImovel)
          } catch (folderError) {
            console.error('Erro ao criar pasta do imóvel no Meu Driver:', folderError)
            showError(
              'Pasta não criada automaticamente',
              `O imóvel foi criado, mas não foi possível gerar a pasta "${nomeImovel}" no Meu Driver. Crie manualmente.`
            )
          }
        } else {
          console.warn('Usuário não autenticado ao tentar criar pasta do imóvel.')
        }
      }

      // Fechar modal e remover blur imediatamente
      setIsModalOpen(false)
      setGlobalModalOpen(false)
      setEditingImovel(null)
      setFormData({ nome: '', endereco: '', estado: '', cidade: '', tipo: '', tipo_imovel: 'novo', valor_venda: '', construtora_id: '', unidades_disponiveis: '', unidades_vendidas: '' })
      setCidades([])
      loadImoveis()

      // Mostrar toast de sucesso após fechar modal
      if (editingImovel) {
        showSuccess(
          'Imóvel atualizado com sucesso!',
          `O imóvel "${nomeImovel}" foi atualizado.`
        )
      } else {
        showSuccess(
          'Imóvel criado com sucesso!',
          `O imóvel "${nomeImovel}" foi criado.`
        )
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar imóvel')
      showError(
        'Erro ao salvar imóvel',
        'Não foi possível salvar o imóvel. Tente novamente.'
      )
    }
  }

  const handleEdit = (imovel: Imovel) => {
    setEditingImovel(imovel)
    setFormData({
      nome: imovel.nome,
      endereco: imovel.endereco || '',
      estado: imovel.estado || '',
      cidade: imovel.cidade || '',
      tipo: imovel.tipo,
      tipo_imovel: imovel.tipo_imovel,
      valor_venda: (imovel.valor_venda || 0).toString(),
      construtora_id: imovel.construtora_id || '',
      unidades_disponiveis: imovel.unidades_disponiveis.toString(),
      unidades_vendidas: imovel.unidades_vendidas.toString()
    })
    if (imovel.estado) {
      loadCidadesPorEstado(imovel.estado)
    } else {
      setCidades([])
    }
    setIsViewMode(false)
    setIsModalOpen(true)
    setGlobalModalOpen(true)
  }

  const handleView = (imovel: Imovel) => {
    setEditingImovel(imovel)
    setFormData({
      nome: imovel.nome,
      endereco: imovel.endereco || '',
      estado: imovel.estado || '',
      cidade: imovel.cidade || '',
      tipo: imovel.tipo,
      tipo_imovel: imovel.tipo_imovel,
      valor_venda: (imovel.valor_venda || 0).toString(),
      construtora_id: imovel.construtora_id || '',
      unidades_disponiveis: imovel.unidades_disponiveis.toString(),
      unidades_vendidas: imovel.unidades_vendidas.toString()
    })
    if (imovel.estado) {
      loadCidadesPorEstado(imovel.estado)
    } else {
      setCidades([])
    }
    setIsViewMode(true)
    setIsModalOpen(true)
    setGlobalModalOpen(true)
  }

  const handleDelete = (imovel: Imovel) => {
    setImovelToDelete(imovel)
    setIsDeleteModalOpen(true)
    setGlobalModalOpen(true)
  }

  const confirmDelete = async () => {
    if (!imovelToDelete) return

    try {
      const { error } = await supabase
        .from('imoveis')
        .delete()
        .eq('id', imovelToDelete.id)

      if (error) throw error

      loadImoveis()

      // Mostrar toast de sucesso
      showSuccess(
        'Imóvel excluído com sucesso!',
        `O imóvel "${imovelToDelete.nome}" foi excluído.`
      )

      // Fechar modal de confirmação
      setIsDeleteModalOpen(false)
      setGlobalModalOpen(false)
      setImovelToDelete(null)
    } catch (err: any) {
      setError(err.message || 'Erro ao excluir imóvel')
      showError(
        'Erro ao excluir imóvel',
        'Não foi possível excluir o imóvel. Tente novamente.'
      )
    }
  }

  const cidadeQuery = formData.cidade?.toLowerCase() ?? ''
  const cidadeSuggestions = (cidadeQuery
    ? cidades.filter(cidade =>
      cidade.toLowerCase().includes(cidadeQuery)
    )
    : cidades
  ).slice(0, 20)

  const cancelDelete = () => {
    setIsDeleteModalOpen(false)
    setGlobalModalOpen(false)
    setImovelToDelete(null)
  }

  const openCreateModal = () => {
    setEditingImovel(null)
    setFormData({ nome: '', endereco: '', estado: '', cidade: '', tipo: 'apartamento', tipo_imovel: 'novo', valor_venda: '', construtora_id: '', unidades_disponiveis: '', unidades_vendidas: '' })
    setCidades([])
    setIsViewMode(false)
    setIsModalOpen(true)
    setGlobalModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setGlobalModalOpen(false)
    setEditingImovel(null)
    setFormData({ nome: '', endereco: '', estado: '', cidade: '', tipo: 'apartamento', tipo_imovel: 'novo', valor_venda: '', construtora_id: '', unidades_disponiveis: '', unidades_vendidas: '' })
    setCidades([])
    setError('')
    setIsViewMode(false)
  }

  // Funções para gerenciar tipos de imóveis
  const openTiposModal = () => {
    setEditingTipo(null)
    setTipoFormData({ nome: '' })
    setError('')
    setIsTiposModalOpen(true)
    setGlobalModalOpen(true)
  }

  const closeTiposModal = () => {
    setIsTiposModalOpen(false)
    setGlobalModalOpen(false)
    setEditingTipo(null)
    setTipoFormData({ nome: '' })
    setError('')
  }

  const handleTipoSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    try {
      if (!tipoFormData.nome || tipoFormData.nome.trim() === '') {
        setError('Nome do tipo é obrigatório')
        return
      }

      if (editingTipo) {
        // Editar tipo existente
        const { error } = await supabase
          .from('tipos_imoveis')
          .update({ nome: tipoFormData.nome.trim() })
          .eq('id', editingTipo.id)

        if (error) throw error
      } else {
        // Criar novo tipo
        const { error } = await supabase
          .from('tipos_imoveis')
          .insert({ nome: tipoFormData.nome.trim() })

        if (error) throw error
      }

      closeTiposModal()
      loadTiposImoveis()
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar tipo')
    }
  }

  const handleEditTipo = (tipo: TipoImovel) => {
    setEditingTipo(tipo)
    setTipoFormData({ nome: tipo.nome })
    setIsTiposModalOpen(true)
    setGlobalModalOpen(true)
  }

  const handleDeleteTipo = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este tipo de imóvel?')) return

    try {
      const { error } = await supabase
        .from('tipos_imoveis')
        .delete()
        .eq('id', id)

      if (error) throw error

      loadTiposImoveis()
    } catch (err: any) {
      setError(err.message || 'Erro ao excluir tipo')
    }
  }

  // Filtros
  const filteredImoveis = imoveis.filter(imovel => {
    const matchesSearch =
      imovel.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (imovel.endereco || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (imovel.estado && imovel.estado.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (imovel.cidade && imovel.cidade.toLowerCase().includes(searchTerm.toLowerCase())) ||
      imovel.construtora?.nome?.toLowerCase().includes(searchTerm.toLowerCase())

    return matchesSearch
  })


  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR')
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center dark:bg-gray-900 min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Carregando imóveis...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 lg:p-6 dark:bg-gray-900 min-h-screen">
      {/* Conteúdo principal com blur quando modal aberta */}
      <div className={`transition-all duration-300 ${isModalOpen || isTiposModalOpen || isDeleteModalOpen ? 'blur-sm opacity-50' : ''}`}>
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Imóveis</h1>
              <p className="text-gray-500 dark:text-gray-400">Gerencie todos os imóveis do sistema</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={openTiposModal}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Tag size={20} />
                Adicionar Tipos
              </button>
              <Button
                variant="btn_add"
                onClick={openCreateModal}
                className="flex items-center gap-2"
              >
                <Plus size={20} />
                Novo Imóvel
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Building className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total de Imóveis</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{imoveis.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Building className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Imóveis Vendidos</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {imoveis.filter(imovel => (imovel.unidades_vendidas || 0) > 0).length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                <input
                  type="text"
                  placeholder="Buscar por nome, endereço, estado, cidade ou construtora..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>
            </div>
            {/* Toggle de Visualização */}
            <div className="flex items-center gap-2">
              <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('cards')}
                  className={`p-2 rounded-md transition-colors ${viewMode === 'cards'
                    ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  title="Visualização em Cards"
                >
                  <Grid3x3 size={20} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors ${viewMode === 'list'
                    ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  title="Visualização em Lista"
                >
                  <List size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <p className="text-red-700 dark:text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* Visualização de Imóveis */}
        {viewMode === 'cards' ? (
          <ImoveisCardView
            imoveis={filteredImoveis}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            formatDate={formatDate}
          />
        ) : (
          <ImoveisListView
            imoveis={filteredImoveis}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            formatDate={formatDate}
            searchTerm={searchTerm}
          />
        )}
      </div>

      {/* Modal de Criar/Editar/Visualizar */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {isViewMode ? 'Visualizar Imóvel' : editingImovel ? 'Editar Imóvel' : 'Novo Imóvel'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    <p className="text-red-700 dark:text-red-400">{error}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-6">
                {/* Nome do Imóvel */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nome do Imóvel *
                  </label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="Ex: Edifício Residencial Sunset"
                    required
                    disabled={isViewMode}
                  />
                </div>

                {/* Endereço */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Endereço Completo <span className="text-gray-400 font-normal">(opcional)</span>
                  </label>
                  <textarea
                    value={formData.endereco}
                    onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                    className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="Opcional — pode preencher depois. Ex: Rua das Palmeiras, 123 - Copacabana, Rio de Janeiro - RJ"
                    rows={3}
                    disabled={isViewMode}
                  />
                </div>

                {/* Estado e Cidade */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Estado *
                    </label>
                    <select
                      value={formData.estado}
                      onChange={(e) => handleEstadoChange(e.target.value)}
                      className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      required
                      disabled={isViewMode}
                    >
                      <option value="">Selecione o estado</option>
                      {loadingEstados ? (
                        <option value="" disabled>
                          Carregando estados...
                        </option>
                      ) : (
                        estados.map((estado) => (
                          <option key={estado.sigla} value={estado.sigla}>
                            {estado.sigla} - {estado.nome}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Cidade *
                    </label>
                    <input
                      type="text"
                      value={formData.cidade}
                      onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                      list="cidades-list"
                      className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="Ex: São Paulo"
                      required
                      disabled={isViewMode || !formData.estado}
                    />
                    {!isViewMode && (
                      <datalist id="cidades-list">
                        {cidadeSuggestions.map((cidade) => (
                          <option key={cidade} value={cidade} />
                        ))}
                      </datalist>
                    )}
                    {loadingCidades && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Carregando cidades...</p>
                    )}
                  </div>
                </div>

                {/* Tipo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tipo do Imóvel *
                  </label>
                  <select
                    value={formData.tipo}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                    className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    required
                    disabled={isViewMode}
                  >
                    <option value="">Selecione um tipo</option>
                    {tiposImoveis.map((tipo) => (
                      <option key={tipo.id} value={tipo.nome}>
                        {tipo.nome}
                      </option>
                    ))}
                  </select>
                  {tiposImoveis.length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Nenhum tipo cadastrado.
                      <button
                        type="button"
                        onClick={openTiposModal}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 underline ml-1"
                      >
                        Clique aqui para adicionar tipos
                      </button>
                    </p>
                  )}
                </div>

                {/* Condição do Imóvel (Novo/Usado) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Condição do Imóvel *
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="tipo_imovel"
                        value="novo"
                        checked={formData.tipo_imovel === 'novo'}
                        onChange={(e) => setFormData({ ...formData, tipo_imovel: e.target.value })}
                        className="w-4 h-4 text-indigo-600 border-gray-300 dark:border-gray-600 focus:ring-indigo-500 dark:bg-gray-700"
                        disabled={isViewMode}
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Novo</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="tipo_imovel"
                        value="usado"
                        checked={formData.tipo_imovel === 'usado'}
                        onChange={(e) => setFormData({ ...formData, tipo_imovel: e.target.value })}
                        className="w-4 h-4 text-indigo-600 border-gray-300 dark:border-gray-600 focus:ring-indigo-500 dark:bg-gray-700"
                        disabled={isViewMode}
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Usado</span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Valor Base */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Valor Base *
                    </label>
                    <CurrencyInputV2
                      value={formData.valor_venda}
                      onChange={(value) => setFormData({ ...formData, valor_venda: value })}
                      placeholder="R$ 0,00"
                      required
                      disabled={isViewMode}
                    />
                  </div>

                  {/* Construtora */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Construtora *
                    </label>
                    <select
                      value={formData.construtora_id}
                      onChange={(e) => setFormData({ ...formData, construtora_id: e.target.value })}
                      className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      required
                      disabled={isViewMode}
                    >
                      <option value="">Selecione uma construtora</option>
                      {construtoras.map((construtora) => (
                        <option key={construtora.id} value={construtora.id}>
                          {construtora.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Unidades */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Unidades Disponíveis */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Unidades Disponíveis
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.unidades_disponiveis}
                      onChange={(e) => setFormData({ ...formData, unidades_disponiveis: e.target.value })}
                      className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="0"
                      disabled={isViewMode}
                    />
                  </div>

                  {/* Unidades Vendidas */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Unidades Vendidas
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.unidades_vendidas}
                      onChange={(e) => setFormData({ ...formData, unidades_vendidas: e.target.value })}
                      className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="0"
                      disabled={isViewMode}
                    />
                  </div>
                </div>

                {/* Informações adicionais no modo visualização */}
                {isViewMode && editingImovel && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Total de Unidades</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{editingImovel.unidades_count || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Unidades Vendidas</p>
                      <p className="text-lg font-bold text-green-600 dark:text-green-400">{editingImovel.unidades_vendidas || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Unidades Disponíveis</p>
                      <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{editingImovel.unidades_disponiveis || 0}</p>
                    </div>
                  </div>
                )}
              </div>

              {!isViewMode && (
                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn-primary flex items-center gap-2"
                  >
                    <Save size={20} />
                    {editingImovel ? 'Atualizar' : 'Criar'} Imóvel
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Modal de Gerenciar Tipos de Imóveis */}
      {isTiposModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {editingTipo ? 'Editar Tipo de Imóvel' : 'Gerenciar Tipos de Imóveis'}
              </h2>
              <button
                onClick={closeTiposModal}
                className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {/* Formulário para adicionar/editar tipo */}
              <form onSubmit={handleTipoSubmit} className="mb-6">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nome do Tipo *
                    </label>
                    <input
                      type="text"
                      value={tipoFormData.nome}
                      onChange={(e) => setTipoFormData({ ...tipoFormData, nome: e.target.value })}
                      className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="Ex: Apartamento, Casa, Terreno..."
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="submit"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors"
                    >
                      <Save size={20} />
                      {editingTipo ? 'Atualizar' : 'Adicionar'}
                    </button>
                  </div>
                </div>
              </form>

              {/* Lista de tipos existentes */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Tipos Cadastrados</h3>

                {tiposImoveis.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Tag className="w-12 h-12 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                    <p>Nenhum tipo de imóvel cadastrado</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {tiposImoveis.map((tipo) => (
                      <div key={tipo.id} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                              <Tag className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-gray-100">{tipo.nome}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Criado em {new Date(tipo.created_at).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditTipo(tipo)}
                              className="p-2 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
                              title="Editar"
                            >
                              <Edit3 size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteTipo(tipo.id)}
                              className="p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                              title="Excluir"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Mensagem de erro */}
              {error && (
                <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    <p className="text-red-800 dark:text-red-400 text-sm">{error}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
            </div>

            <div className="text-center mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                Confirmar Exclusão
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Tem certeza que deseja excluir este imóvel? Todas as unidades relacionadas também serão excluídas.
              </p>
              {imovelToDelete && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-4 text-left">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    <strong>Imóvel:</strong> {imovelToDelete.nome}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    <strong>Endereço:</strong> {imovelToDelete.endereco?.trim() || 'Não informado'}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    <strong>Tipo:</strong> {imovelToDelete.tipo}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    <strong>Unidades:</strong> {imovelToDelete.unidades_disponiveis} disponíveis, {imovelToDelete.unidades_vendidas} vendidas
                  </p>
                </div>
              )}
              <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                ⚠️ Esta operação não pode ser revertida
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors font-medium"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} />
    </div>
  )
}